import AppLayout from "../components/AppLayout";
import { useState } from "react";
import RoundedButton from "../components/buttons/RoundedButton";
import AlertMsg from "../components/info/AlertMsg";
import BarChart from "../components/charts/BarChart";
import NoDataMsg from "../components/info/NoDataMsg";
import { IoMdAnalytics } from "react-icons/io";
import Spinner from "../components/Spinner";
import { useAnalyticsChatsEvaluation } from "../hooks/useAnalyticsChatsEvaluation";
import { FiClock } from "react-icons/fi";
import TableChatsEvaluation from "../components/tables/TableChatsEvaluation";
import ToggleTab from "../components/tabs/ToggleTab";
import { RiRobot2Fill } from "react-icons/ri";
import { BiSupport } from "react-icons/bi";
import { useEffect } from "react";
import CardContainer from "../components/cards/CardContainer";

const ChatsEvaluation = () => {
    const {
        conversationLimit,
        setConversationLimit,
        isGenerating,
        generationProgress,
        evaluationData,
        lastGeneratedData,
        selectedFrequencyMetric,
        setSelectedFrequencyMetric,
        generalMetrics,
        scoreMetricOptions,
        frequencyChartData,
        handleGenerateEvaluation,
        CONVERSATION_LIMIT_OPTIONS,
    } = useAnalyticsChatsEvaluation();

    const [viewTypeTab, setViewTypeTab] = useState(1); // 1 for bot evaluation, 2 for human attendant evaluation

    const metricLabels = (() => {
        const labels = {};

        // Extraer etiquetas del chatbot
        if (evaluationData?.general?.chatbot) {
            Object.entries(evaluationData.general.chatbot).forEach(([key, value]) => {
                if (value?.name) {
                    labels[key] = value.name;
                }
            });
        }

        // Extraer etiquetas del operador
        if (evaluationData?.general?.operator) {
            Object.entries(evaluationData.general.operator).forEach(([key, value]) => {
                if (value?.name) {
                    labels[key] = value.name;
                }
            });
        }

        return labels;
    })();

    const filteredMetrics = generalMetrics.filter((metric) => {
        if (viewTypeTab === 1) return metric.type === "chatbot";
        if (viewTypeTab === 2) return metric.type === "operator";
        return true;
    });

    const filteredScoreMetricOptions = scoreMetricOptions.filter((option) => {
        const metricKey = option.key;

        if (viewTypeTab === 1) {
            return evaluationData?.general?.chatbot?.[metricKey] !== undefined;
        }
        if (viewTypeTab === 2) {
            return evaluationData?.general?.operator?.[metricKey] !== undefined;
        }
        return true;
    });

    useEffect(() => {
        // Resetear la métrica seleccionada cuando cambia de solapa
        if (filteredScoreMetricOptions.length > 0) {
            const isCurrentMetricValid = filteredScoreMetricOptions.some(
                (option) => option.key === selectedFrequencyMetric
            );
            if (!isCurrentMetricValid) {
                setSelectedFrequencyMetric(filteredScoreMetricOptions[0].key);
            }
        }
    }, [viewTypeTab, filteredScoreMetricOptions, selectedFrequencyMetric, setSelectedFrequencyMetric]);

    const hasPreviousEvaluation = Boolean(
        (evaluationData?.details && evaluationData.details.length > 0) ||
        Number(evaluationData?.general?.evaluated_conversations || 0) > 0
    );

    const getScoreColorStyles = (value) => {
        if (value > 7) {
            return {
                fill: "#2f9e44",
                track: "rgba(47, 158, 68, 0.18)",
                text: "#2f9e44",
            };
        }

        if (value >= 5) {
            return {
                fill: "#b08900",
                track: "rgba(176, 137, 0, 0.18)",
                text: "#b08900",
            };
        }

        return {
            fill: "#e03131",
            track: "rgba(224, 49, 49, 0.18)",
            text: "#e03131",
        };
    };

    return (
        <AppLayout
            title="Analíticas"
            subtitle="Explora los datos analíticos de tu chatbot. Obtén información
                    detallada sobre el comportamiento de tus clientes, patrones de
                    conversación y métricas clave para mejorar y potenciar la eficacia
                    de las interacciones."
            tab={3}
            tabType="analytics"
        >
            <AlertMsg
                type="info"
                text={"La evaluación de conversaciones utiliza inteligencia artificial para analizar el contenido. Este proceso genera consumo de tokens y los costos resultantes pueden visualizarse en la sección de facturación."}
            />
            <div className="row gx-0 gap-2 mt-3">
                <div className="row gx-0 djaBetween mb-2">
                    <div className="col pt-3">
                        <h3 className="m-0 ms-3">
                            <strong>Resumen de analíticas</strong>
                        </h3>
                    </div>
                    <div className="col-md-auto col-12 me-3">
                        <p className="ms-3" style={{ lineHeight: "1rem" }}>
                            Cantidad de conversaciones
                        </p>
                        <select
                            style={{ width: "18rem" }}
                            onChange={(e) =>
                                setConversationLimit(Number(e.target.value))
                            }
                        >
                            {CONVERSATION_LIMIT_OPTIONS.map((limit) => (
                                <option key={limit} value={limit}>
                                    Últimas {limit}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-auto row gx-0 d-flex gap-2" aria-label="filter">
                        <div className="col-md-auto col-12 d-flex align-items-end justify-content-end pt-3">
                            <RoundedButton
                                label={isGenerating ? "Generando evaluación..." : "Generar evaluación"}
                                color="trini"
                                handleClick={handleGenerateEvaluation}
                                disabled={isGenerating}
                                loading={isGenerating}
                                style={{ height: "40px" }}
                            />
                        </div>
                    </div>
                </div>

                {isGenerating ? (
                    <Spinner
                        message={`Generando evaluación... ${generationProgress?.current || "-"}/${generationProgress?.total || "-"}`}
                        description="Esto puede tardar varios minutos dependiendo de la cantidad de conversaciones a evaluar."
                    />
                ) : hasPreviousEvaluation ? (
                    <>
                        {/* {lastGeneratedData?.generated_date && ( */}
                        <div className="d-flex justify-content-between align-items-end">
                            <ToggleTab
                                setTab={setViewTypeTab}
                                tab={viewTypeTab}
                                items={[
                                    { icon: RiRobot2Fill, label: "Chatbot", value: 1 },
                                    { icon: BiSupport, label: "Atención Humana", value: 2 }
                                ]}
                            />

                            <div
                                className="d-flex align-items-center gap-1 me-1"
                                style={{
                                    fontSize: "12px",
                                    color: "var(--text-color-secondary)",
                                    cursor: "pointer",
                                }}
                                title={`Última evaluación generada el ${lastGeneratedData?.generated_date?.date || "-"} a las ${lastGeneratedData?.generated_date?.time || "-"}hs`}
                            >
                                <FiClock />
                                <strong>Última evaluación generada:</strong>
                                <i>{lastGeneratedData?.generated_date?.relative}</i>
                            </div>
                        </div>
                        {/* )} */}

                        {viewTypeTab === 1 && (
                            <>
                                <CardContainer
                                    softTitle="PARÁMETROS GENERALES"
                                    mainTitle="Resultados generales de la evaluación"
                                    description={`Estos son los resultados generales de la evaluación realizada a las últimas ${evaluationData?.general?.evaluated_conversations || 0} conversaciones. La barra de progreso muestra el puntaje promedio obtenido en cada métrica, facilitando la identificación de áreas fuertes y oportunidades de mejora en tu chatbot.`}
                                >
                                    <div className="m-4" >
                                        {evaluationData?.general?.chatbot && (
                                            <div
                                                style={{
                                                    border: "1px solid var(--separator-color)",
                                                    borderRadius: "10px",
                                                    overflow: "hidden",
                                                    backgroundColor: "var(--card-color)",
                                                    color: "var(--title-color)",
                                                }}
                                            >
                                                <div
                                                    className="d-none d-md-flex"
                                                    style={{
                                                        borderBottom: "1px solid var(--separator-color)",
                                                        backgroundColor: "var(--card-secondary-color)",
                                                    }}
                                                >
                                                    <div style={{ flex: "1", color: "var(--title-color)", fontWeight: 600, padding: "12px 14px" }}>
                                                        Métrica
                                                    </div>
                                                    <div style={{ flex: "1", color: "var(--title-color)", fontWeight: 600, padding: "12px 14px", textAlign: "center" }}>
                                                        Evaluación
                                                    </div>
                                                    <div style={{ flex: "2", color: "var(--title-color)", fontWeight: 600, padding: "12px 14px" }}>
                                                        Descripción
                                                    </div>
                                                </div>

                                                {filteredMetrics.map((metric, index) => {
                                                    const scoreColorStyles = getScoreColorStyles(metric.avg);

                                                    return (
                                                        <div
                                                            key={metric.key}
                                                            className="d-flex flex-column flex-md-row"
                                                            style={{
                                                                borderBottom:
                                                                    index !== filteredMetrics.length - 1
                                                                        ? "1px solid var(--separator-color)"
                                                                        : "none",
                                                            }}
                                                        >
                                                            <div style={{ flex: "1", padding: "14px", fontWeight: 500, textTransform: "capitalize", color: "var(--title-color)" }}>
                                                                {metric.name}
                                                            </div>

                                                            <div style={{ flex: "1", padding: "14px" }}>
                                                                <div className="d-flex align-items-center justify-content-md-center" style={{ gap: "8px" }}>
                                                                    <div
                                                                        style={{
                                                                            width: "120px",
                                                                            height: "8px",
                                                                            backgroundColor: scoreColorStyles.track,
                                                                            borderRadius: "999px",
                                                                            overflow: "hidden",
                                                                        }}
                                                                    >
                                                                        <div
                                                                            style={{
                                                                                width: `${Math.max(
                                                                                    0,
                                                                                    Math.min(100, (metric.avg / 10) * 100)
                                                                                )}%`,
                                                                                height: "100%",
                                                                                backgroundColor: scoreColorStyles.fill,
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <p style={{ margin: 0, fontWeight: 500, color: scoreColorStyles.text, fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                                                                        {metric.avg.toFixed(1)}/10
                                                                    </p>
                                                                </div>
                                                            </div>

                                                            <div style={{ flex: "2", padding: "14px", color: "var(--text-color)", fontSize: "0.95rem" }}>
                                                                {metric.description}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </CardContainer>
                                <CardContainer
                                    mainTitle="Detalle por conversación"
                                    softTitle="ANÁLISIS DE PARAMETROS"
                                    description="Distribución de las conversaciones evaluadas según el puntaje obtenido en la métrica seleccionada. La barra representa la cantidad de conversaciones que cayeron dentro de cada rango de puntaje, mientras que la línea verde muestra el porcentaje acumulado de conversaciones hasta ese rango. Esto te permite identificar rápidamente cómo se distribuyen los puntajes y qué porcentaje de conversaciones alcanza ciertos niveles de desempeño."
                                >
                                    <div
                                        className="m-4"
                                    >
                                        {evaluationData?.details?.length > 0 && (
                                            <div>
                                                <select
                                                    value={selectedFrequencyMetric}
                                                    onChange={(e) => setSelectedFrequencyMetric(e.target.value)}
                                                >
                                                    {filteredScoreMetricOptions.map((metricOption) => (
                                                        <option key={metricOption.key} value={metricOption.key}>
                                                            {metricOption.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                {frequencyChartData && (
                                                    <div style={{ height: "280px" }} className="my-3">
                                                        <BarChart
                                                            chartData={frequencyChartData}
                                                            options={{
                                                                maintainAspectRatio: false,
                                                                plugins: {
                                                                    legend: {
                                                                        display: true,
                                                                    },
                                                                },
                                                                scales: {
                                                                    y: {
                                                                        beginAtZero: true,
                                                                        ticks: { precision: 0 },
                                                                        title: {
                                                                            display: true,
                                                                            text: "Cantidad de conversaciones",
                                                                        },
                                                                    },
                                                                    y1: {
                                                                        beginAtZero: true,
                                                                        max: 100,
                                                                        position: "right",
                                                                        grid: {
                                                                            drawOnChartArea: false,
                                                                        },
                                                                        ticks: {
                                                                            callback: (value) => `${value}%`,
                                                                        },
                                                                        title: {
                                                                            display: true,
                                                                            text: "Acumulado (%)",
                                                                        },
                                                                    },
                                                                    x: {
                                                                        title: {
                                                                            display: true,
                                                                            text: "Rango de puntaje",
                                                                        },
                                                                    },
                                                                },
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </CardContainer>
                                <CardContainer
                                    softTitle="ANÁLISIS"
                                    mainTitle="Evaluación de conversaciones"
                                    description="Listado de todas las conversaciones evaluadas, mostrando el puntaje obtenido en cada métrica. Esto permite un análisis detallado de cada interacción, facilitando la identificación de conversaciones con alto o bajo desempeño y proporcionando información valiosa para mejorar la experiencia del usuario con tu chatbot."
                                >
                                    <div className="m-4">
                                        {evaluationData?.details?.length > 0 && (
                                            <TableChatsEvaluation
                                                data={evaluationData.details}
                                                metricLabels={metricLabels}
                                                metricType="chatbot"
                                            />
                                        )}
                                    </div>
                                </CardContainer>
                            </>
                        )}
                        {viewTypeTab === 2 && (
                            <>
                                <CardContainer
                                    softTitle="PARÁMETROS GENERALES"
                                    mainTitle="Resultados generales de la evaluación"
                                    description={`Estos son los resultados generales de la evaluación realizada a las últimas ${evaluationData?.general?.evaluated_conversations || 0} conversaciones. La barra de progreso muestra el puntaje promedio obtenido en cada métrica, facilitando la identificación de áreas fuertes y oportunidades de mejora en tu chatbot.`}
                                >
                                    <div className="m-4">
                                        {evaluationData?.general?.operator && (
                                            <div
                                                style={{
                                                    border: "1px solid var(--separator-color)",
                                                    borderRadius: "10px",
                                                    overflow: "hidden",
                                                    backgroundColor: "var(--card-color)",
                                                    color: "var(--title-color)",
                                                }}
                                            >
                                                <div
                                                    className="d-none d-md-flex"
                                                    style={{
                                                        borderBottom: "1px solid var(--separator-color)",
                                                        backgroundColor: "var(--card-secondary-color)",
                                                    }}
                                                >
                                                    <div style={{ flex: "1", color: "var(--title-color)", fontWeight: 600, padding: "12px 14px" }}>
                                                        Métrica
                                                    </div>
                                                    <div style={{ flex: "1", color: "var(--title-color)", fontWeight: 600, padding: "12px 14px", textAlign: "center" }}>
                                                        Evaluación
                                                    </div>
                                                    <div style={{ flex: "2", color: "var(--title-color)", fontWeight: 600, padding: "12px 14px" }}>
                                                        Descripción
                                                    </div>
                                                </div>

                                                {filteredMetrics.map((metric, index) => {
                                                    const scoreColorStyles = getScoreColorStyles(metric.avg);

                                                    return (
                                                        <div
                                                            key={metric.key}
                                                            className="d-flex flex-column flex-md-row"
                                                            style={{
                                                                borderBottom:
                                                                    index !== filteredMetrics.length - 1
                                                                        ? "1px solid var(--separator-color)"
                                                                        : "none",
                                                            }}
                                                        >
                                                            <div style={{ flex: "1", padding: "14px", fontWeight: 500, textTransform: "capitalize", color: "var(--title-color)" }}>
                                                                {metric.name}
                                                            </div>

                                                            <div style={{ flex: "1", padding: "14px" }}>
                                                                <div className="d-flex align-items-center justify-content-md-center" style={{ gap: "8px" }}>
                                                                    <div
                                                                        style={{
                                                                            width: "120px",
                                                                            height: "8px",
                                                                            backgroundColor: scoreColorStyles.track,
                                                                            borderRadius: "999px",
                                                                            overflow: "hidden",
                                                                        }}
                                                                    >
                                                                        <div
                                                                            style={{
                                                                                width: `${Math.max(
                                                                                    0,
                                                                                    Math.min(100, (metric.avg / 10) * 100)
                                                                                )}%`,
                                                                                height: "100%",
                                                                                backgroundColor: scoreColorStyles.fill,
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <p style={{ margin: 0, fontWeight: 500, color: scoreColorStyles.text, fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                                                                        {metric.avg.toFixed(1)}/10
                                                                    </p>
                                                                </div>
                                                            </div>

                                                            <div style={{ flex: "2", padding: "14px", color: "var(--text-color)", fontSize: "0.95rem" }}>
                                                                {metric.description}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </CardContainer>

                                <CardContainer
                                    softTitle="ANÁLISIS DE PARAMETROS"
                                    mainTitle="Detalle por conversación"
                                    description="Distribución de las conversaciones evaluadas según el puntaje obtenido en la métrica seleccionada. La barra representa la cantidad de conversaciones que cayeron dentro de cada rango de puntaje, mientras que la línea verde muestra el porcentaje acumulado de conversaciones hasta ese rango. Esto te permite identificar rápidamente cómo se distribuyen los puntajes y qué porcentaje de conversaciones alcanza ciertos niveles de desempeño."
                                >
                                    <div className="m-4">
                                        {evaluationData?.details?.length > 0 && (
                                            <>
                                                <select
                                                    value={selectedFrequencyMetric}
                                                    onChange={(e) => setSelectedFrequencyMetric(e.target.value)}
                                                >
                                                    {filteredScoreMetricOptions.map((metricOption) => (
                                                        <option key={metricOption.key} value={metricOption.key}>
                                                            {metricOption.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                {frequencyChartData && (
                                                    <div style={{ height: "280px" }} className="my-3">
                                                        <BarChart
                                                            chartData={frequencyChartData}
                                                            options={{
                                                                maintainAspectRatio: false,
                                                                plugins: {
                                                                    legend: {
                                                                        display: true,
                                                                    },
                                                                },
                                                                scales: {
                                                                    y: {
                                                                        beginAtZero: true,
                                                                        ticks: { precision: 0 },
                                                                        title: {
                                                                            display: true,
                                                                            text: "Cantidad de conversaciones",
                                                                        },
                                                                    },
                                                                    y1: {
                                                                        beginAtZero: true,
                                                                        max: 100,
                                                                        position: "right",
                                                                        grid: {
                                                                            drawOnChartArea: false,
                                                                        },
                                                                        ticks: {
                                                                            callback: (value) => `${value}%`,
                                                                        },
                                                                        title: {
                                                                            display: true,
                                                                            text: "Acumulado (%)",
                                                                        },
                                                                    },
                                                                    x: {
                                                                        title: {
                                                                            display: true,
                                                                            text: "Rango de puntaje",
                                                                        },
                                                                    },
                                                                },
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </CardContainer>
                                <CardContainer
                                    softTitle="ANÁLISIS"
                                    mainTitle="Evaluación de conversaciones"
                                    description="Listado de todas las conversaciones evaluadas, mostrando el puntaje obtenido en cada métrica. Esto permite un análisis detallado de cada interacción, facilitando la identificación de conversaciones con alto o bajo desempeño y proporcionando información valiosa para mejorar la experiencia del usuario con tu chatbot."
                                >
                                    <div className="m-4">
                                        {evaluationData?.details?.length > 0 && (
                                            <TableChatsEvaluation
                                                data={evaluationData.details}
                                                metricLabels={metricLabels}
                                                metricType="operator"
                                            />
                                        )}
                                    </div>
                                </CardContainer>
                            </>
                        )}
                    </>
                ) : (
                    <div className="card p-3">
                        <NoDataMsg
                            title="No hay resultados para mostrar"
                            icon={<IoMdAnalytics />}
                            subtitle="Aún no se ha generado una evaluación de conversaciones. Haz clic en el botón 'Generar evaluación' para analizar las últimas conversaciones de tu chatbot y obtener información valiosa sobre su desempeño."
                        />
                    </div>
                )}
            </div>
        </AppLayout>
    );
};

export default ChatsEvaluation;
